import { ChevronRight } from "lucide-react";
import {
  useEffect,
  useId,
  useRef,
  useState,
  type KeyboardEvent,
  type PointerEvent,
  type ReactNode
} from "react";
import { cn } from "../../lib/cn";

export function ChartAxis({ start, end }: { start: ReactNode; end: ReactNode }) {
  return (
    <div className="ui-chart-axis" aria-hidden="true">
      <span>{start}</span>
      <span>{end}</span>
    </div>
  );
}

export const TREND_CHART_INSET_LEFT = 58;
export const TREND_CHART_INSET_RIGHT = 12;

export function trendChartDomain(points: number[]) {
  if (points.length === 0) return { min: 0, max: 1 };
  const dataMin = Math.min(...points);
  const dataMax = Math.max(...points);
  if (dataMin === 0 && dataMax === 0) return { min: 0, max: 1 };
  return {
    min: Math.min(dataMin, 0),
    max: Math.max(dataMax, 0)
  };
}

export function trendChartIndexAtPointer(pointerX: number, plotWidth: number, pointCount: number) {
  if (pointCount <= 1) return 0;
  const plotStart = TREND_CHART_INSET_LEFT;
  const plotEnd = Math.max(plotStart, plotWidth - TREND_CHART_INSET_RIGHT);
  const clampedX = Math.min(Math.max(pointerX, plotStart), plotEnd);
  const ratio = plotEnd === plotStart ? 0 : (clampedX - plotStart) / (plotEnd - plotStart);
  return Math.min(pointCount - 1, Math.max(0, Math.round(ratio * (pointCount - 1))));
}

export function TrendChart({
  points,
  labels,
  valueLabels,
  axisValueLabel = (value) => String(Math.round(value)),
  title,
  description
}: {
  points: number[];
  labels?: string[];
  valueLabels?: string[];
  axisValueLabel?: (value: number) => string;
  title: string;
  description: string;
}) {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [width, setWidth] = useState(640);
  const interactionDescriptionID = useId();
  const shellRef = useRef<HTMLDivElement | null>(null);
  const height = 220;
  const insetLeft = TREND_CHART_INSET_LEFT;
  const insetRight = TREND_CHART_INSET_RIGHT;
  const insetTop = 16;
  const insetBottom = 20;
  const { max, min } = trendChartDomain(points);
  const spread = Math.max(max - min, 1);
  const plotHeight = height - insetTop - insetBottom;
  const valueY = (value: number) => height - insetBottom - ((value - min) / spread) * plotHeight;
  const zeroY = valueY(0);
  const coords = points.map((value, index) => {
    const x = points.length === 1 ? insetLeft : insetLeft + (index / (points.length - 1)) * (width - insetLeft - insetRight);
    const y = valueY(value);
    return { x, y, value };
  });
  const axisValues = min < 0 && max > 0
    ? [max, 0, min]
    : [max, min + spread / 2, min];
  const axisTicks = axisValues
    .filter((value, index) => axisValues.findIndex((candidate) => Math.abs(candidate - value) < 0.0001) === index)
    .map((value) => ({ value, y: valueY(value) }));
  const activePoint = activeIndex === null ? undefined : coords[activeIndex];
  const activeLabel = activeIndex === null ? undefined : labels?.[activeIndex] ?? `Point ${activeIndex + 1}`;
  const activeValue = activeIndex === null ? undefined : valueLabels?.[activeIndex] ?? String(points[activeIndex]);

  useEffect(() => {
    const shell = shellRef.current;
    if (!shell) return;
    const updateWidth = () => setWidth(Math.max(insetLeft + insetRight + 80, Math.round(shell.getBoundingClientRect().width)));
    updateWidth();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(updateWidth);
    observer.observe(shell);
    return () => observer.disconnect();
  }, [insetLeft, insetRight]);

  function updateFromPointer(event: PointerEvent<HTMLDivElement>) {
    if (points.length === 0) return;
    const bounds = event.currentTarget.getBoundingClientRect();
    const pointerX = Math.min(Math.max(event.clientX - bounds.left, 0), bounds.width);
    const chartX = bounds.width === 0 ? 0 : pointerX * (width / bounds.width);
    setActiveIndex(trendChartIndexAtPointer(chartX, width, points.length));
  }

  function handleKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (points.length === 0) return;
    const current = activeIndex ?? points.length - 1;
    let next = current;
    if (event.key === "ArrowLeft") next = Math.max(0, current - 1);
    else if (event.key === "ArrowRight") next = Math.min(points.length - 1, current + 1);
    else if (event.key === "Home") next = 0;
    else if (event.key === "End") next = points.length - 1;
    else return;
    event.preventDefault();
    setActiveIndex(next);
  }

  return (
    <div
      ref={shellRef}
      className="ui-trend-chart-shell"
      role="group"
      tabIndex={0}
      aria-label={`Explore ${title}`}
      aria-describedby={interactionDescriptionID}
      onFocus={() => setActiveIndex((current) => current ?? Math.max(points.length - 1, 0))}
      onBlur={() => setActiveIndex(null)}
      onKeyDown={handleKeyDown}
      onPointerMove={updateFromPointer}
      onPointerLeave={() => setActiveIndex(null)}
    >
      <span className="sr-only" id={interactionDescriptionID}>
        {description} Use the left and right arrow keys to inspect each date.
      </span>
      <svg className="ui-trend-chart" viewBox={`0 0 ${width} ${height}`} role="img" aria-label={`${title}. ${description}`}>
        <title>{title}</title>
        <desc>{description}</desc>
        {axisTicks.map((tick, index) => (
          <g key={`${tick.value}-${index}`} aria-hidden="true">
            <line className="ui-trend-chart-grid" x1={insetLeft} x2={width - insetRight} y1={tick.y} y2={tick.y} />
            <text className="ui-trend-chart-axis-label" x={insetLeft - 8} y={tick.y + 4} textAnchor="end">
              {axisValueLabel(tick.value)}
            </text>
          </g>
        ))}
        {min < 0 && max > 0 ? (
          <line className="ui-trend-chart-zero" x1={insetLeft} x2={width - insetRight} y1={zeroY} y2={zeroY} aria-hidden="true" />
        ) : null}
        <polygon
          className="ui-trend-chart-fill"
          points={`${insetLeft},${zeroY} ${coords.map(({ x, y }) => `${x},${y}`).join(" ")} ${width - insetRight},${zeroY}`}
          aria-hidden="true"
        />
        <polyline
          className="ui-trend-chart-line"
          points={coords.map(({ x, y }) => `${x},${y}`).join(" ")}
          fill="none"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        {coords.map((coord, index) => (
          <circle
            key={`${labels?.[index] ?? index}-${coord.value}`}
            className={cn("ui-trend-chart-point", activeIndex === index && "ui-trend-chart-point-active")}
            cx={coord.x}
            cy={coord.y}
            r={activeIndex === index ? 5 : 3}
            aria-hidden="true"
          />
        ))}
      </svg>
      {activePoint && activeLabel && activeValue ? (
        <output
          className={cn(
            "ui-trend-chart-tooltip",
            activePoint.y < 64 && "ui-trend-chart-tooltip-below",
            activePoint.x < 72 && "ui-trend-chart-tooltip-edge-left",
            activePoint.x > width - 72 && "ui-trend-chart-tooltip-edge-right"
          )}
          style={{ left: `${(activePoint.x / width) * 100}%`, top: `${(activePoint.y / height) * 100}%` }}
          aria-live="polite"
        >
          <span>{activeLabel}</span>
          <strong>{activeValue}</strong>
        </output>
      ) : null}
    </div>
  );
}

export type BucketChartRow = {
  label: string;
  value: number;
  tone?: "default" | "attention";
  actionLabel?: string;
  onSelect?: () => void;
};

export function BucketChart({
  ariaLabel,
  rows,
  valueLabel
}: {
  ariaLabel: string;
  rows: BucketChartRow[];
  valueLabel: (row: BucketChartRow) => string;
}) {
  const max = Math.max(...rows.map((row) => row.value), 1);

  return (
    <div className="ui-bucket-chart" role="list" aria-label={ariaLabel}>
      {rows.map((row) => {
        const content = (
          <>
            <span className="ui-bucket-chart-bar-track" aria-hidden="true">
              <span
                className={cn("ui-bucket-chart-bar", row.tone === "attention" && "ui-bucket-chart-bar-attention")}
                style={{ height: row.value === 0 ? "0%" : `${Math.max(8, (row.value / max) * 100)}%` }}
              />
            </span>
            <small className="ui-bucket-chart-label">
              <strong>{row.value}</strong>
              <span>{row.label}</span>
            </small>
            {row.onSelect ? (
              <span className="ui-bucket-chart-action" aria-hidden="true">
                View <ChevronRight />
              </span>
            ) : (
              <span className="ui-bucket-chart-action ui-bucket-chart-action-static" aria-hidden="true" />
            )}
          </>
        );

        return (
          <div key={row.label} className="ui-bucket-chart-item" role="listitem">
            {row.onSelect ? (
              <button
                className="ui-bucket-chart-control"
                type="button"
                aria-label={row.actionLabel ?? `View ${valueLabel(row)} aged ${row.label} in inventory`}
                onClick={row.onSelect}
              >
                {content}
              </button>
            ) : (
              <div className="ui-bucket-chart-control ui-bucket-chart-control-static" aria-label={`${row.label}: ${valueLabel(row)}`}>
                {content}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
